import styled from '@emotion/styled';
import { Button, Input, Modal, Select, Space, Table, notification } from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { FaPen, FaTrash } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { ConnectedUserEntity } from '../../../../../entities/ConnectedUserEntity';
import { BondEntity } from '../../../../../entities/bond.entity';
import { OperationEntity } from '../../../../../entities/operation.entity';
import { PRIMARY } from '../../../../../shared/colors';
import { formatNum } from '../../../../../shared/constants';
import { deleteOperation, updateOperation } from '../network/network';

const Container = styled.div`
  color: white;
  padding: 5px;
  margin-bottom: 5px;
  font-family: Poppins-R;

  th {
    background-color: ${PRIMARY} !important;
    color: white !important;
    font-weight: bold;
  }

  max-height: calc(100vh - 575px);
  overflow-x: hidden;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 0.5vw;
  }

  &::-webkit-scrollbar-thumb {
    background-image: linear-gradient(to bottom, #eceff1, #eceff1);
    border-radius: 10px;
  }
`;

export const Orders = ({
  orders,
  updateOders,
  getSelectedBond,
}: {
  orders: any[];
  updateOders: () => void;
  getSelectedBond: (bond: BondEntity) => void;
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showModifModal, setShowModifModal] = useState(false);
  const [selectedBond, setSelectedBond] = useState<BondEntity>();
  const [selectedOPE, setSelectedOPE] = useState<OperationEntity>();
  const [isLoading, setIsLoading] = useState(false);
  const [lmtOptions, setLmtOptions] = useState<any[]>([]);
  const [operation, setOperation] = useState({
    qte: 0,
    prix: 0,
    decote: 0,
  });
  const connectedUser: ConnectedUserEntity = useSelector(
    (state: any) => state.userReducer,
  ).user;
  interface PriceItem {
    value: number;
    label: string;
  }
  useEffect(() => {
    if (selectedBond) {
      let prices: PriceItem[] = [];
      let percent = 0.85;
      let val = 0;
      while (percent <= 1.16) {
        val = Math.floor(selectedBond.valeurNominale * percent);
        prices.push({
          value: val,
          label: `${Math.floor(percent * 100)}% - ${formatNum(val)} XAF`,
        });
        percent += 0.01;
      }

      setLmtOptions(prices);
    }
  }, [selectedBond]);

  const deleteOffer = () => {
    setIsLoading(true);
    if (selectedOPE) {
      deleteOperation(connectedUser.id, selectedOPE.id).then(
        ({ statusCode, data }) => {
          if (statusCode?.toString()[0] === '2') {
            notification.success({
              message: 'Offre supprimé',
              description: 'Votre ordre a été supprimé avec succès',
            });
            updateOders();
          } else {
            notification.info({
              message: 'Erreur',
              description: data.message,
            });
          }
          setShowDeleteModal(false);
          setIsLoading(false);
        },
      );
    }
  };

  const modifOffer = () => {
    setIsLoading(true);
    if (selectedOPE) {
      updateOperation(connectedUser.id, selectedOPE.id, operation).then(
        ({ statusCode, data }) => {
          if (statusCode.toString()[0] === '2') {
            notification.success({
              message: 'Offre mise à jour',
              description: 'Votre ordre a été mise à jour avec succès',
            });
            // afficher le statuts des matchs
            notification.info({
              message: "Statut de l'ordre",
              description: data.match.message,
            });
            updateOders();
          } else {
            notification.error({
              message: 'Erreur',
              description: data.message,
            });
          }
          setIsLoading(false);
          setShowModifModal(false);
        },
      );
    }
  };

  const COLUMNS = [
    {
      title: 'Pays',
      render: (cell: any, row: OperationEntity) => row.bond.pays,
    },
    {
      title: 'CODE',
      render: (cell: any, row: OperationEntity) => row.bond.code,
    },
    {
      title: 'Detenteur',
      render: (cell: any, row: OperationEntity) => row.detenteur?.code_referent,
    },
    {
      title: 'Opération',
      dataIndex: 'typeOperation',
    },
    {
      title: 'Date Ope',
      dataIndex: 'dateOperation',
      render: (cell: any) => `${moment(cell).format('DD/MM/YYYY')}`,
    },
    {
      title: 'Qte',
      dataIndex: 'qteRestant',
      render: (cell: any, row: OperationEntity) => `${formatNum(cell)}`,
    },
    {
      title: 'Coupon',
      dataIndex: 'tauxInteret',
    },
    {
      title: 'Décote',
      dataIndex: 'decote',
    },
    {
      title: 'Montant',
      render: (cell: any, row: OperationEntity) =>
        `${formatNum(row.prixNominale * 1)}`,
    },
    {
      title: 'Prix total',
      render: (cell: any, row: OperationEntity) =>
        `${formatNum(row.qteRestant * row.prixNominale)}`,
    },
    {
      title: 'Net',
      render: (cell: any, row: OperationEntity) =>
        `${formatNum(
          Math.ceil(
            Number(row.qteRestant) * Number(row.prixNominale) +
              Number(row.couponCouruPayer) +
              Number(row.commIntermediation) +
              Number(row.commConservation) +
              Number(row.commDroitGarde) +
              Number(row.commBrokerage),
          ),
        )}`,
    },
    {
      title: 'Action',
      render: (cell: any, row: OperationEntity) => (
        <Space>
          <Button
            icon={<FaPen style={{ color: 'white', marginBottom: -2 }} />}
            style={{
              backgroundColor: 'transparent',
              borderRadius: 20,
              zoom: 0.8,
            }}
            onClick={() => {
              setSelectedBond(row.bond);
              getSelectedBond(row.bond);
              setSelectedOPE(row);
              setShowModifModal(true);
            }}
          />
          <Button
            icon={<FaTrash style={{ color: 'white', marginBottom: -2 }} />}
            style={{
              backgroundColor: 'transparent',
              borderRadius: 20,
              zoom: 0.8,
            }}
            onClick={() => {
              setSelectedBond(row.bond);
              getSelectedBond(row.bond);
              setSelectedOPE(row);
              setShowDeleteModal(true);
            }}
          />
        </Space>
      ),
    },
  ];

  return (
    <Container>
      {orders.length === 0 ? (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          Aucun ordre n'a été transmis...
        </div>
      ) : (
        <Table
          columns={COLUMNS}
          dataSource={orders}
          rowClassName={(row, index) =>
            index % 2 === 0 ? 'even-row' : 'odd-row'
          }
          size='small'
          style={{ zoom: 0.75 }}
          pagination={false}
        />
      )}

      <Modal
        className='dark-modal'
        open={showDeleteModal}
        title="Supprimer l'Ordre"
        onCancel={() => setShowDeleteModal(false)}
        footer={[
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              danger
              onClick={() => setShowDeleteModal(false)}
              style={{ border: '2px solid red', borderRadius: 20 }}
            >
              Non
            </Button>
            <Button
              className='butt'
              loading={isLoading}
              style={{ borderRadius: 20 }}
              onClick={deleteOffer}
            >
              Oui
            </Button>
          </div>,
        ]}
      >
        <p>Voulez-vous supprimer cette ordre ?</p>
      </Modal>

      <Modal
        className='dark-modal'
        open={showModifModal}
        title="Modifier l'Ordre"
        onCancel={() => setShowModifModal(false)}
        footer={[
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              danger
              onClick={() => setShowModifModal(false)}
              style={{ border: '2px solid red', borderRadius: 20 }}
            >
              Fermer
            </Button>
            <Button
              className='butt'
              loading={isLoading}
              style={{ borderRadius: 20 }}
              onClick={modifOffer}
            >
              Valider
            </Button>
          </div>,
        ]}
      >
        <Space>
          <div>
            <p style={{ marginBottom: 10 }}>Prix d'achat : </p>
            <p style={{ margin: 0 }}>Quantité : </p>
          </div>
          <div>
            <Select
              size='small'
              value={operation.prix}
              style={{ width: '100%', marginBottom: 15 }}
              options={lmtOptions}
              onChange={(val) =>
                setOperation({
                  ...operation,
                  prix: Number(val),
                  decote: 100 - val / 100,
                })
              }
            />

            <Input
              className='qteInput'
              placeholder='qte'
              value={operation.qte}
              type='number'
              min={0}
              onChange={(e) =>
                setOperation({
                  ...operation,
                  qte: Number(e.target.value),
                })
              }
            />
          </div>
        </Space>
      </Modal>
    </Container>
  );
};
